import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useMotionValue, useTransform } from "framer-motion";
import logoWhite from "@/assets/logo-white.svg";
import AnimatedMenuButton from "@/components/ui/AnimatedMenuButton";
import FullPageMenu from "@/components/FullPageMenu";
import EarlyAccessModal from "@/components/EarlyAccessModal";

const navLinks = [
  { label: "Lineup", href: "/lineup" },
  { label: "Schedule", href: "/schedule" },
  { label: "Guide", href: "/guide" },
  { label: "Contact", href: "/contact" },
];

// Cursor-following nav underline component
const SlidingNav = ({ links }: { links: { label: string; href: string; comingSoon?: boolean }[] }) => {
  const navRef = useRef<HTMLDivElement>(null);
  const lineX = useMotionValue(0);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const navRect = navRef.current?.getBoundingClientRect();
    if (navRect) {
      const x = e.clientX - navRect.left;
      lineX.set(x);
    }
  };

  const lineLeft = useTransform(lineX, (x) => x - 40);

  return (
    <div
      ref={navRef}
      className="hidden md:block relative"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      <nav className="flex items-stretch justify-center gap-0">
        {links.map((link) => {
          const isActive = location.pathname === link.href;
          
          if (link.comingSoon) {
            return (
              <span
                key={link.label}
                className="text-white/30 text-sm font-medium tracking-widest uppercase px-8 py-4 flex items-center cursor-not-allowed"
              >
                {link.label}
              </span>
            );
          }
          
          return (
            <Link
              key={link.label}
              to={link.href}
              className={`text-sm font-medium tracking-widest uppercase px-8 py-4 flex items-center transition-colors ${
                isActive ? "text-white" : "text-white/70 hover:text-white"
              }`}
            >
              {link.label}
            </Link>
          );
        })}
      </nav>
      
      <motion.span
        className="absolute bottom-0 h-px bg-white pointer-events-none"
        style={{
          left: lineLeft,
          width: 80,
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.15s ease-out",
        }}
      />
    </div>
  );
};

interface GlobalHeaderProps {
  heroRef?: React.RefObject<HTMLElement>;
  ticketsCta?: {
    label?: string;
    href?: string;
    onClick?: () => void;
  };
}

const GlobalHeader = ({ heroRef, ticketsCta }: GlobalHeaderProps) => {
  const ticketsLabel = ticketsCta?.label ?? "Get Tickets";
  const ticketsHref = ticketsCta?.href ?? "/tickets";
  const handleTicketsClick = ticketsCta?.onClick ?? (() => { window.location.href = ticketsHref; });
  const [menuOpen, setMenuOpen] = useState(false);
  const [earlyAccessOpen, setEarlyAccessOpen] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Sticky nav show/hide logic - same as homepage
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const heroHeight = heroRef?.current?.offsetHeight || window.innerHeight;
      const isInHero = currentScrollY < heroHeight;
      const isScrollingUp = currentScrollY < lastScrollY.current;

      if (isInHero || isScrollingUp) {
        setStickyVisible(true);
      } else {
        setStickyVisible(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [heroRef]);

  // Listen for early access modal trigger
  useEffect(() => {
    const handleOpenEarlyAccess = () => setEarlyAccessOpen(true);
    window.addEventListener('openEarlyAccess', handleOpenEarlyAccess);
    return () => window.removeEventListener('openEarlyAccess', handleOpenEarlyAccess);
  }, []);

  return (
    <>
      <FullPageMenu 
        isOpen={menuOpen} 
        onClose={() => setMenuOpen(false)} 
        onJoinWaitlist={() => setEarlyAccessOpen(true)} 
      />
      <EarlyAccessModal 
        isOpen={earlyAccessOpen} 
        onClose={() => setEarlyAccessOpen(false)} 
      />

      {/* STICKY: Only Menu + Get Tickets buttons */}
      <div 
        className={`fixed top-0 left-0 right-0 z-[100] pointer-events-none ${
          stickyVisible && !menuOpen
            ? 'translate-y-0 opacity-100' 
            : '-translate-y-8 opacity-0'
        }`}
        style={{
          transition: stickyVisible && !menuOpen
            ? 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease-out'
            : 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease-in-out'
        }}
      >
        <div className="w-full px-6 md:px-12 lg:px-16 py-8 md:py-6 flex items-center justify-between">
          <div className="pointer-events-auto">
            <AnimatedMenuButton onClick={() => setMenuOpen(true)} />
          </div>
          
          <div className="pointer-events-auto">
            <button
              onClick={handleTicketsClick}
              className="btn-primary px-7 py-3 md:px-8 md:py-3.5 text-base font-semibold"
            >
              {ticketsLabel}
            </button>
          </div>
        </div>
      </div>
      
      {/* NON-STICKY: Full Header with Logo, Dates, Nav - stays at top of hero, scrolls away */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <div>
          <div className="w-full px-6 md:px-12 lg:px-16 py-8 md:py-6">
            {/* Top row: Menu | Logo | Get Tickets */}
            <div className="flex items-center justify-between relative">
              {/* Invisible spacer for menu button */}
              <div className="flex-shrink-0 opacity-0 pointer-events-none">
                <AnimatedMenuButton onClick={() => {}} />
              </div>
              
              {/* Logo with flanking dates - center (desktop only) */}
              <div className="hidden md:flex items-center justify-center gap-4 md:gap-8 absolute left-1/2 -translate-x-1/2">
                <span className="text-white/70 text-xs md:text-sm font-light tracking-[0.2em] w-16 md:w-20 text-right">JULY 17-18</span>
                <Link to="/" className="block">
                  <img src={logoWhite} alt="Yacht Rock Detroit" className="h-24 md:h-32 w-auto" />
                </Link>
                <span className="text-white/70 text-xs md:text-sm font-light tracking-[0.2em] w-16 md:w-20 text-left">2026</span>
              </div>
              
              {/* Invisible spacer for Join Waitlist */}
              <div className="flex-shrink-0 opacity-0 pointer-events-none">
                <span className="btn-primary px-5 py-2.5 md:px-6 md:py-2 text-sm font-semibold">
                  Get Tickets
                </span>
              </div>
            </div>
          </div>
          
          {/* Divider line */}
          <div className="h-px bg-white/20" />
          
          {/* Navigation row - desktop only */}
          <div className="w-full">
            <SlidingNav links={navLinks} />
          </div>
          
          {/* Bottom divider line */}
          <div className="h-px bg-white/20" />
        </div>
      </header>
    </>
  );
};

export default GlobalHeader;
